import { z } from 'zod';
/**
 * Die Verträge der Datensicherung mit Auslagerung — Task 1 `[DS-grund]` des
 * Plans `docs/superpowers/plans/2026-10-06-datensicherung-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-10-06-datensicherung-design.md`, Abschnitte 4,
 * 5, 6.5 und 8.1.
 *
 * **Hier stehen die Aufzählungen und Formen, die Datenbank, API und Schiene
 * teilen** — wörtlich dieselben Wertesätze wie die CHECK-Bedingungen in
 * Migration `0280_sicherung.sql`. Die Schemas der sechzehn Routen unter
 * `/api/v1/sicherung` (Spec 8.1) stehen seit der Task `DS-api` im zweiten
 * Teil dieser Datei.
 *
 * **Bytes sind nie `number`** (Spec 8.1, H6): `bytes_neu`, `bytes_gesamt` und
 * die Grössen im Konfigurationsabzug sind `bigint` und gehen als Zeichenkette
 * aus Ziffern über die Leitung — dieselbe Haltung wie bei Beträgen: kein Wert,
 * der jenseits von 2⁵³ still ungenau würde.
 */
/** Die vier Laufarten (Spec 4.3). */
export const SICHERUNG_LAUF_ARTEN = ['lokal', 'auslagerung', 'pruefung', 'wiederherstellung'];
export const sicherungLaufArtSchema = z.enum(SICHERUNG_LAUF_ARTEN);
/**
 * Die fünf Zustände eines Laufs (Spec 4.3) — dieselben fünf trägt eine
 * Anforderung (Spec 4.4). Ein Wertesatz, zwei Namen: Wer einen Zustand
 * hinzufügt, fügt ihn beiden Tabellen hinzu, und das soll an einer Stelle
 * geschehen.
 */
export const SICHERUNG_ZUSTAENDE = ['angefordert', 'laeuft', 'ok', 'fehler', 'abgebrochen'];
export const sicherungLaufErgebnisSchema = z.enum(SICHERUNG_ZUSTAENDE);
export const sicherungAnforderungZustandSchema = sicherungLaufErgebnisSchema;
/**
 * Die zwanzig Fehlerarten (Spec 6.5, `D-17`) — gehalten von den
 * Bedingungen `sicherung_lauf_fehlerart_bekannt` und
 * `sicherung_anforderung_fehlerart_bekannt` in `0280`.
 */
export const SICHERUNG_FEHLERARTEN = [
    'ziel_nicht_erreichbar',
    'anmeldung_abgewiesen',
    'hostschluessel_abweichend',
    'repository_passwort_falsch',
    'repository_vorhanden',
    'zielpfad_fremd',
    'staende_abweichend',
    'repository_gesperrt',
    'vorabfrage_gescheitert',
    'dump_gescheitert',
    'leseprobe_gescheitert',
    'backup_gescheitert',
    'forget_gescheitert',
    'prune_gescheitert',
    'check_befund',
    'verwahrung_unlesbar',
    'platz_erschoepft',
    'prozess_beendet',
    'zeitueberschreitung',
    'unbekannt',
];
export const sicherungFehlerartSchema = z.enum(SICHERUNG_FEHLERARTEN);
/** Die vier Anforderungsarten (Spec 4.4, `D-33`). */
export const SICHERUNG_ANFORDERUNG_ARTEN = ['schluesselpaar', 'zielpruefung', 'zielanlage', 'zielfassung'];
export const sicherungAnforderungArtSchema = z.enum(SICHERUNG_ANFORDERUNG_ARTEN);
/** Wie eine Fassung des Ziels entstand (Spec 4.1, `D-38`). */
export const SICHERUNG_HERKUENFTE = ['einrichtung', 'fortschreibung', 'wiederherstellung'];
export const sicherungHerkunftSchema = z.enum(SICHERUNG_HERKUENFTE);
/** Der Zugang zum SFTP-Konto (Spec 5.2, `D-12`). */
export const SICHERUNG_ZUGANGSARTEN = ['schluessel', 'passwort'];
export const sicherungZugangsartSchema = z.enum(SICHERUNG_ZUGANGSARTEN);
/** Ob eine Zielanlage ein neues Repository anlegt oder ein vorhandenes einbindet (Spec 5.1). */
export const SICHERUNG_ANLAGE_MODI = ['neu', 'einbinden'];
export const sicherungAnlageModusSchema = z.enum(SICHERUNG_ANLAGE_MODI);
/**
 * Die Arten eines Host-Schlüssels, **in der Rangfolge der Wahl** (Spec 5.3,
 * `D-13`): `ed25519` vor `ecdsa` vor `rsa`. Die Reihenfolge dieser Liste IST
 * die Rangfolge — wer sie umstellt, ändert, welcher Schlüssel bestätigt wird.
 */
export const SICHERUNG_HOSTSCHLUESSEL_ARTEN = ['ed25519', 'ecdsa', 'rsa'];
export const sicherungHostschluesselArtSchema = z.enum(SICHERUNG_HOSTSCHLUESSEL_ARTEN);
/** Das Sicherungskennzeichen: 8 Zufallsbytes als 16 Hexziffern (`D-48`, `sicherung_ziel_kennzeichen_form`). */
export const sicherungKennzeichenSchema = z
    .string()
    .regex(/^[0-9a-f]{16}$/u, 'Ein Sicherungskennzeichen hat 16 Hexziffern');
/** Bytes als Zeichenkette aus Ziffern (Spec 8.1) — nie `number`. */
export const sicherungBytesSchema = z.string().regex(/^[0-9]+$/u, 'Bytes als Zeichenkette aus Ziffern erwartet');
/** Ein Fingerabdruck wie `ssh-keygen -l`: `SHA256:` und 43 Zeichen base64 ohne Auffüllung. */
export const sicherungFingerabdruckSchema = z
    .string()
    .regex(/^SHA256:[A-Za-z0-9+/]{43}$/u, 'Fingerabdruck in der Form SHA256:… erwartet');
/** Der Prüfumfang einer wöchentlichen Prüfung: `n/12` (`D-44`, `sicherung_lauf_pruefumfang_form`). */
export const sicherungPruefumfangSchema = z
    .string()
    .regex(/^([1-9]|1[0-2])\/12$/u, 'Prüfumfang in der Form n/12 erwartet');
// ===========================================================================
// Die sechzehn Routen unter `/api/v1/sicherung` (Spec 8.1) — Task 5 `[DS-api]`
// ===========================================================================
//
// **Was keine Antwort trägt:** einen privaten Schlüssel (`E-1776`), ein
// Chiffrat, das Repository-Passwort ausser in `abholen` und
// `passwort-anzeigen` (`E-1747`), Pfad und Benutzer des Ziels ausser in
// `GET …/ziel` (`E-1767`). Jede Antwort ist `.strict()` — ein Feld, das der
// Dienst versehentlich mitgäbe, fiele im Vertragswächter auf (`E-1773`).
/** Ein Zeitpunkt, ISO-8601 — die Form der übrigen Antworten dieses Vertrags. */
const zeitpunkt = z.string();
const kennung = z.string().uuid('Keine gültige UUID');
/** Die Uhrzeit der täglichen Auslagerung in UTC, `HH:MM` (Spec 4.1, `D-15`). */
export const sicherungUhrzeitSchema = z
    .string()
    .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/u, 'Uhrzeit in der Form HH:MM (UTC) erwartet');
/** Was am Zielpfad liegt: nichts, ein Repository oder Fremdes (Spec 5.1). */
export const SICHERUNG_PFADLAGEN = ['leer', 'repository', 'fremd'];
export const sicherungPfadlageSchema = z.enum(SICHERUNG_PFADLAGEN);
/** Die drei Zustände einer Fassung des Ziels (Spec 4.1). */
export const SICHERUNG_FASSUNGSZUSTAENDE = ['unbestaetigt', 'geltend', 'beendet'];
export const sicherungFassungszustandSchema = z.enum(SICHERUNG_FASSUNGSZUSTAENDE);
/** Die Ampel einer Laufart — dieselben vier Werte und dieselbe Rechnung wie im Systemstatus. */
export const SICHERUNG_AMPELN = ['gruen', 'gelb', 'rot', 'unbekannt'];
export const sicherungAmpelSchema = z.enum(SICHERUNG_AMPELN);
// --- GET /api/v1/sicherung/status ------------------------------------------
/** Eine Laufart in der Sicht `sicherung_offen` (Spec 4.5). */
export const sicherungLaufLageSchema = z
    .object({
    art: sicherungLaufArtSchema,
    /** Ampel und Hinweis aus der Rechnung des Systemstatus; `wiederherstellung` hat keine Ampel (`unbekannt`). */
    ampel: sicherungAmpelSchema,
    hinweis: z.string().nullable(),
    letzterErfolgAm: zeitpunkt.nullable(),
    letzterVersuchAm: zeitpunkt.nullable(),
    letztesErgebnis: sicherungLaufErgebnisSchema.nullable(),
    fehlerSeitErfolg: z.number().int().nonnegative(),
    staendeImZiel: z.number().int().nonnegative().nullable(),
    aeltesterStandAm: zeitpunkt.nullable(),
    bytesGesamt: sicherungBytesSchema.nullable(),
})
    .strict();
/**
 * Das geltende Ziel im Status — **ohne Pfad und Benutzer** (`E-1767`): Der
 * Status ist auch über ein API-Token erreichbar, Pfad und Benutzer stehen nur
 * in `GET …/ziel` (Sitzung) und auf der Karte.
 */
export const sicherungStatusZielSchema = z
    .object({
    id: kennung,
    herkunft: sicherungHerkunftSchema,
    art: z.literal('sftp'),
    host: z.string(),
    port: z.number().int(),
    zugangsart: sicherungZugangsartSchema,
    hostschluesselFingerabdruck: sicherungFingerabdruckSchema,
    uhrzeitUtc: sicherungUhrzeitSchema,
    bestaetigtAm: zeitpunkt,
})
    .strict();
/** Eine unbestätigte Fassung (Einrichtung nicht abgeschlossen oder nach einer Wiederherstellung). */
export const sicherungStatusUnbestaetigtSchema = z
    .object({ id: kennung, herkunft: sicherungHerkunftSchema, angelegtAm: zeitpunkt })
    .strict();
/** Die zuletzt beendete Fassung ohne Nachfolger, nur wenn keine gilt (`D-43`). */
export const sicherungStatusBeendetSchema = z
    .object({ id: kennung, beendetAm: zeitpunkt, beendetVon: kennung.nullable() })
    .strict();
/** Eine Fassung der Aufbewahrung (Spec 4.2). */
export const sicherungAufbewahrungSchema = z
    .object({
    tageTaeglich: z.number().int().min(1),
    wochen: z.number().int().min(0),
    monate: z.number().int().min(0),
    gueltigAb: zeitpunkt,
})
    .strict();
/** `GET /api/v1/sicherung/status` — die Sicht `sicherung_offen`, aufbereitet. */
export const sicherungStatusSchema = z
    .object({
    erhobenAm: zeitpunkt,
    /** Die schlechteste Ampel von lokal, Auslagerung und Prüfung — dieselbe Rechnung wie der Systemstatus. */
    ampel: sicherungAmpelSchema,
    laeufe: z.array(sicherungLaufLageSchema),
    ziel: sicherungStatusZielSchema.nullable(),
    unbestaetigt: sicherungStatusUnbestaetigtSchema.nullable(),
    beendet: sicherungStatusBeendetSchema.nullable(),
    aufbewahrung: sicherungAufbewahrungSchema.nullable(),
})
    .strict();
// --- GET /api/v1/sicherung/laeufe ------------------------------------------
/** Höchstzahl der Zeilen in `GET …/laeufe` (Spec 8.1). */
export const SICHERUNG_LAEUFE_HOECHSTENS = 200;
/** Die Abfragezeile von `GET …/laeufe`: `?art=&limit=`. */
export const sicherungLaeufeAnfrageSchema = z
    .object({
    art: sicherungLaufArtSchema.optional(),
    limit: z.coerce.number().int().min(1).max(SICHERUNG_LAEUFE_HOECHSTENS).optional(),
})
    .strict();
/** Ein Lauf in der Laufliste — Bytes als Ziffern, Fehlertext bereinigt (`E-1767`). */
export const sicherungLaufSchema = z
    .object({
    id: kennung,
    art: sicherungLaufArtSchema,
    ergebnis: sicherungLaufErgebnisSchema,
    zielId: kennung.nullable(),
    angefordertVon: kennung.nullable(),
    angelegtAm: zeitpunkt,
    beginnAm: zeitpunkt.nullable(),
    endeAm: zeitpunkt.nullable(),
    schnappschuss: z.string().nullable(),
    kennzeichen: sicherungKennzeichenSchema.nullable(),
    dateiname: z.string().nullable(),
    bytesNeu: sicherungBytesSchema.nullable(),
    bytesGesamt: sicherungBytesSchema.nullable(),
    staendeImZiel: z.number().int().nonnegative().nullable(),
    aeltesterStandAm: zeitpunkt.nullable(),
    pruefumfang: sicherungPruefumfangSchema.nullable(),
    geraeumt: z.boolean().nullable(),
    ausgeschlossen: z.array(z.string()).nullable(),
    fehlerart: sicherungFehlerartSchema.nullable(),
    fehlertext: z.string().nullable(),
})
    .strict();
/** `GET /api/v1/sicherung/laeufe` — neueste zuerst. */
export const sicherungLaufListeSchema = z.object({ laeufe: z.array(sicherungLaufSchema) }).strict();
// --- GET /api/v1/sicherung/ziel --------------------------------------------
/** Eine Fassung des Ziels — nie ein Chiffrat, nie ein Klartext (Spec 8.1). */
export const sicherungZielFassungSchema = z
    .object({
    id: kennung,
    ersetztZielId: kennung.nullable(),
    herkunft: sicherungHerkunftSchema,
    zustand: sicherungFassungszustandSchema,
    kennzeichen: sicherungKennzeichenSchema,
    art: z.literal('sftp'),
    host: z.string(),
    port: z.number().int(),
    pfad: z.string(),
    benutzer: z.string(),
    zugangsart: sicherungZugangsartSchema,
    oeffentlicherSchluessel: z.string().nullable(),
    hostschluesselFingerabdruck: sicherungFingerabdruckSchema,
    uhrzeitUtc: sicherungUhrzeitSchema,
    angelegtAm: zeitpunkt,
    angelegtVon: kennung.nullable(),
    bestaetigtAm: zeitpunkt.nullable(),
    bestaetigtVon: kennung.nullable(),
    beendetAm: zeitpunkt.nullable(),
    beendetVon: kennung.nullable(),
})
    .strict();
/** `GET /api/v1/sicherung/ziel` — geltende und unbestätigte Fassung samt Fassungsliste (neueste zuerst). */
export const sicherungZielAuskunftSchema = z
    .object({
    geltend: sicherungZielFassungSchema.nullable(),
    unbestaetigt: sicherungZielFassungSchema.nullable(),
    fassungen: z.array(sicherungZielFassungSchema),
})
    .strict();
// --- Anforderungen (Spec 4.4) ----------------------------------------------
/** Die Angaben eines SFTP-Ziels — kein Geheimnis. */
const zielAngabe = {
    host: z
        .string()
        .trim()
        .min(1)
        .max(253)
        .regex(/^[A-Za-z0-9.:-]+$/u, 'Host als Name oder Adresse ohne Leerzeichen erwartet'),
    port: z.number().int().min(1).max(65535).default(22),
    pfad: z.string().trim().min(1).max(1024),
    benutzer: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .regex(/^[^\s@]+$/u, 'Benutzer ohne Leerzeichen und ohne @ erwartet'),
};
/**
 * Der Zugang zum SFTP-Konto: Passwort, ein eingefügter privater Schlüssel oder
 * die **Kennung** einer Schlüsselpaar-Anforderung (`E-1776`) — nie der
 * erzeugte Schlüssel selbst.
 */
export const sicherungZugangEingabeSchema = z
    .object({
    zugangsart: sicherungZugangsartSchema,
    passwort: z.string().min(1).max(1024).optional(),
    privaterSchluessel: z.string().min(1).max(16384).optional(),
    schluesselpaarId: kennung.optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    if (wert.zugangsart === 'passwort') {
        if (wert.passwort === undefined) {
            ctx.addIssue({ code: 'custom', path: ['passwort'], message: 'Beim Passwortzugang ist passwort Pflicht' });
        }
        if (wert.privaterSchluessel !== undefined || wert.schluesselpaarId !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['zugangsart'], message: 'Beim Passwortzugang kein Schlüssel' });
        }
        return;
    }
    if (wert.passwort !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['passwort'], message: 'Beim Schlüsselzugang kein Passwort' });
    }
    if ((wert.privaterSchluessel === undefined) === (wert.schluesselpaarId === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['schluesselpaarId'],
            message: 'Beim Schlüsselzugang genau eines: schluesselpaarId oder privaterSchluessel',
        });
    }
});
/** `POST …/ziel/pruefen` — Verbindung, Host-Schlüssel; mit bestätigtem Fingerabdruck auch Anmeldung und Pfadlage. */
export const sicherungZielPruefenSchema = z
    .object({
    ...zielAngabe,
    zugang: sicherungZugangEingabeSchema,
    fingerabdruck: sicherungFingerabdruckSchema.optional(),
})
    .strict();
/** `POST …/ziel/anlegen` — `neu` (das Passwort erzeugt die API) oder `einbinden` (Passwort und Zahl der Stände). */
export const sicherungZielAnlegenSchema = z
    .object({
    ...zielAngabe,
    zugang: sicherungZugangEingabeSchema,
    fingerabdruck: sicherungFingerabdruckSchema,
    uhrzeitUtc: sicherungUhrzeitSchema.optional(),
    modus: sicherungAnlageModusSchema,
    repositoryPasswort: z.string().min(1).max(1024).optional(),
    staende: z.number().int().min(0).optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    if (wert.modus === 'neu') {
        if (wert.repositoryPasswort !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['repositoryPasswort'],
                message: 'Bei modus neu erzeugt das System das Passwort',
            });
        }
        if (wert.staende !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['staende'], message: 'Bei modus neu keine Zahl der Stände' });
        }
        return;
    }
    if (wert.repositoryPasswort === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['repositoryPasswort'],
            message: 'Beim Einbinden ist repositoryPasswort Pflicht',
        });
    }
    if (wert.staende === undefined) {
        ctx.addIssue({ code: 'custom', path: ['staende'], message: 'Beim Einbinden ist staende Pflicht' });
    }
});
/**
 * `POST …/ziel` — eine neue Fassung DESSELBEN Repositorys (Uhrzeit, Zugang,
 * Host nach erneuter Prüfung). Ohne `zugang` übernimmt der `worker` den Zugang
 * der geltenden Fassung.
 */
export const sicherungZielFassungAnfrageSchema = z
    .object({
    ...zielAngabe,
    zugang: sicherungZugangEingabeSchema.optional(),
    fingerabdruck: sicherungFingerabdruckSchema,
    uhrzeitUtc: sicherungUhrzeitSchema.optional(),
})
    .strict();
/**
 * `POST …/ziel/bestaetigen` — die Pflichtbestätigung (`D-2`). Bei einer Fassung
 * aus einer Wiederherstellung zusätzlich, dass diese Instanz die
 * Ursprungsinstallation ersetzt (`D-38`).
 */
export const sicherungBestaetigenSchema = z
    .object({
    passwortAusserhalbAbgelegt: z.literal(true),
    ursprungsinstallationErsetzt: z.literal(true).optional(),
})
    .strict();
/** `POST …/aufbewahrung` — eine neue Fassung (Spec 4.2). */
export const sicherungAufbewahrungAnfrageSchema = z
    .object({
    tageTaeglich: z.number().int().min(1).max(3660),
    wochen: z.number().int().min(0).max(520),
    monate: z.number().int().min(0).max(1200),
})
    .strict();
/**
 * Was die API in `sicherung_anforderung.eingabe` schreibt — **nie ein
 * Geheimnis** (Spec 4.4). Der `worker` liest es; die Schlüssel dieses
 * Objekts sind die Naht zwischen API und Schiene.
 */
export const sicherungAnforderungEingabeSchema = z
    .object({
    host: z.string().optional(),
    port: z.number().int().optional(),
    pfad: z.string().optional(),
    benutzer: z.string().optional(),
    zugangsart: sicherungZugangsartSchema.optional(),
    /** Bei `zielfassung` ohne neuen Zugang: den Zugang der geltenden Fassung übernehmen. */
    zugangUebernehmen: z.boolean().optional(),
    schluesselpaarId: kennung.optional(),
    modus: sicherungAnlageModusSchema.optional(),
    fingerabdruck: sicherungFingerabdruckSchema.optional(),
    staende: z.number().int().min(0).optional(),
    uhrzeitUtc: sicherungUhrzeitSchema.optional(),
    /** Bei `zielfassung`: die geltende Fassung, die fortgeschrieben wird. */
    zielId: kennung.optional(),
})
    .strict();
/**
 * Was verschlüsselt in `sicherung_anforderung.eingabe_chiffrat` steht — ein
 * JSON-Objekt, als Ganzes verwahrt (`apps/api/src/sicherung/verwahrung.ts`).
 */
export const sicherungAnforderungGeheimnisSchema = z
    .object({
    sftpPasswort: z.string().optional(),
    privaterSchluessel: z.string().optional(),
    repositoryPasswort: z.string().optional(),
})
    .strict();
/**
 * Was der `worker` in `sicherung_anforderung.ergebnis` schreibt und die API
 * über `GET …/anforderungen/:id` zeigt — **nie der private Schlüssel**
 * (`E-1776`): Der steht nur als `ergebnis_chiffrat`.
 */
export const sicherungAnforderungErgebnisSchema = z
    .object({
    hostschluesselArt: sicherungHostschluesselArtSchema.optional(),
    fingerabdruck: sicherungFingerabdruckSchema.optional(),
    pfadlage: sicherungPfadlageSchema.optional(),
    staende: z.number().int().min(0).optional(),
    kennzeichen: z.array(z.string()).optional(),
    oeffentlicherSchluessel: z.string().optional(),
    zielId: kennung.optional(),
})
    .strict();
/** `GET …/anforderungen/:id` und die Antwort jeder Route, die eine Anforderung anlegt. */
export const sicherungAnforderungSchema = z
    .object({
    id: kennung,
    art: sicherungAnforderungArtSchema,
    zustand: sicherungAnforderungZustandSchema,
    angelegtAm: zeitpunkt,
    beginnAm: zeitpunkt.nullable(),
    endeAm: zeitpunkt.nullable(),
    ergebnis: sicherungAnforderungErgebnisSchema.nullable(),
    fehlerart: sicherungFehlerartSchema.nullable(),
    fehlertext: z.string().nullable(),
    /** Ob `POST …/abholen` das Repository-Passwort jetzt liefert (genau einmal). */
    passwortAbholbar: z.boolean(),
})
    .strict();
/** `POST …/anforderungen/:id/abholen` und `POST …/ziel/passwort-anzeigen`. */
export const sicherungPasswortSchema = z.object({ repositoryPasswort: z.string() }).strict();
/** Die Antwort von `POST …/lauf` und `POST …/pruefung`: die angelegte Zeile. */
export const sicherungLaufAngefordertSchema = z.object({ lauf: sicherungLaufSchema }).strict();
//# sourceMappingURL=sicherung.js.map